import React, { useState, useEffect } from 'react';
import { getMSPTenants } from '../utils/mspApi';
import SecureGuard from '../../component/user/SecureGuard';
import { Spinner } from 'react-bootstrap';
import toastr from 'toastr';

const API_BASE = 'https://htb4wmezrg.execute-api.us-west-2.amazonaws.com/prod';

const MSPSecurity = ({ mspConfig, isSidebarOpen }) => {
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mspSummary, setMspSummary] = useState(null);
  const [tenantSecurityData, setTenantSecurityData] = useState({});

  useEffect(() => {
    loadMSPData();
  }, []);

  const loadMSPData = async () => {
    try {
      const tenantsData = await getMSPTenants(mspConfig.mspID);
      setTenants(tenantsData.tenants || []);
      
      // Load security data for each tenant
      const securityData = {};
      let totalFindings = 0;
      let totalCritical = 0;
      
      for (const tenant of tenantsData.tenants || []) {
        try {
          const response = await fetch(`${API_BASE}/v2/security/summary`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ tenantID: tenant.tenantID })
          });
          if (response.ok) {
            const data = await response.json();
            securityData[tenant.tenantID] = data;
            // Use the same field names as tenant SecureGuard
            const findings = data.failedChecks || data.totalFindings || 0;
            const critical = data.criticalCount || data.critical || 0;
            totalFindings += findings;
            totalCritical += critical;
          }
        } catch (error) {
          console.error(`Error loading security for tenant ${tenant.tenantID}:`, error);
          securityData[tenant.tenantID] = { failedChecks: 0, criticalCount: 0 };
        }
      }
      setTenantSecurityData(securityData);
      
      setMspSummary({
        totalFindings,
        totalCritical,
        tenantCount: tenantsData.tenants?.length || 0
      });
      
    } catch (error) {
      console.error('Error loading MSP security data:', error);
      toastr.error('Failed to load MSP security data');
    } finally {
      setLoading(false);
    }
  };

  const getTenantDisplayName = (tenant) => {
    return tenant.companyName || tenant.email.split('@')[0];
  };

  const handleTenantSelect = (tenant) => {
    localStorage.setItem('msp_selected_tenant', tenant.tenantID);
    localStorage.setItem('msp_original_tenant', localStorage.getItem('tenantID') || '');
    localStorage.setItem('tenantID', tenant.tenantID);
    setSelectedTenant(tenant);
  };

  const handleBackToMSP = () => {
    const originalTenant = localStorage.getItem('msp_original_tenant');
    if (originalTenant) {
      localStorage.setItem('tenantID', originalTenant);
    } else {
      localStorage.removeItem('tenantID');
    }
    localStorage.removeItem('msp_selected_tenant');
    localStorage.removeItem('msp_original_tenant');
    setSelectedTenant(null);
  };

  if (loading) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 120px)', paddingTop: '80px' }}>
        <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
        <p className="mt-3" style={{ color: '#342ead', fontSize: '1.1rem' }}>Loading MSP Security...</p>
      </div>
    );
  }

  if (tenants.length === 0) {
    return (
      <div className="alert alert-info m-4">
        <h4>No Managed Tenants</h4>
        <p>You don't have any managed tenants yet. Add tenants to view their security data.</p>
      </div>
    );
  }

  // If tenant is selected, show the regular tenant SecureGuard view
  if (selectedTenant) {
    return (
      <div>
        <div className="container-fluid p-2 bg-light border-bottom">
          <button 
            className="btn btn-outline-secondary btn-sm"
            onClick={handleBackToMSP}
          >
            ← Back to MSP Dashboard
          </button>
          <span className="ms-3 text-muted">
            Viewing SecureGuard for: <strong>{getTenantDisplayName(selectedTenant)}</strong>
          </span>
        </div>
        <div style={{ marginTop: '-20px' }}>
          <SecureGuard isSidebarOpen={isSidebarOpen} />
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-4">
      <div className="row mb-4">
        <div className="col-12">
          <h2 className="mb-3">MSP SecureGuard Dashboard</h2>
          <p className="text-muted">Select a tenant to view their detailed security dashboard</p>
        </div>
      </div>
      
      <div className="row mb-4">
        <div className="col-md-4">
          <div className="card">
            <div className="card-body">
              <h6 className="text-muted">Total Security Findings</h6>
              <h3 className="text-danger">{mspSummary?.totalFindings || 0}</h3>
              <small className="text-muted">Across {mspSummary?.tenantCount || 0} tenants</small>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card">
            <div className="card-body">
              <h6 className="text-muted">Critical Findings</h6>
              <h3 className="text-warning">{mspSummary?.totalCritical || 0}</h3>
              <small className="text-muted">Require immediate attention</small>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card">
            <div className="card-body">
              <h6 className="text-muted">Managed Tenants</h6>
              <h3 className="text-secondary">{tenants.length}</h3>
              <small className="text-muted">Active customers</small>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <h4 className="mb-3">Managed Tenants</h4>
        </div>
        {tenants.map(tenant => {
          const securityData = tenantSecurityData[tenant.tenantID] || {};
          const findings = securityData.failedChecks || securityData.totalFindings || 0;
          const critical = securityData.criticalCount || securityData.critical || 0;
          return (
            <div key={tenant.tenantID} className="col-md-6 col-lg-4 mb-4">
              <div 
                className="card h-100 shadow-sm" 
                style={{ cursor: 'pointer', transition: 'transform 0.2s' }}
                onClick={() => handleTenantSelect(tenant)}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <h5 className="card-title mb-0">{getTenantDisplayName(tenant)}</h5>
                    <span className="badge bg-success">Active</span>
                  </div>
                  
                  <p className="text-muted small mb-2">{tenant.email}</p>
                  
                  <div className="row text-center mt-3">
                    <div className="col-6">
                      <h4 className="text-danger mb-0">{findings}</h4>
                      <small className="text-muted">Findings</small>
                    </div>
                    <div className="col-6">
                      <h4 className="text-warning mb-0">{critical}</h4>
                      <small className="text-muted">Critical</small>
                    </div>
                  </div>
                  
                  <div className="mt-3">
                    <small className="text-muted">
                      Plan: {tenant.subscriptionPlan?.plan || 'Basic'} | 
                      Created: {new Date(tenant.createdAt).toLocaleDateString()}
                    </small>
                  </div>
                </div>
                
                <div className="card-footer bg-transparent">
                  <button className="btn btn-sm w-100" style={{ backgroundColor: '#342ead', color: 'white' }}>
                    View SecureGuard Dashboard →
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MSPSecurity;